import type { Bucket } from "@steelyard/theme";
import { formatMoney } from "../../lib/format";

/**
 * The ageing report's data shape and its parsing, shared by `pages/customers/Ageing.tsx` and its
 * `ageing-columns.tsx`. Ported from apps/admin/components/customer-ageing-table.tsx. No fixed bucket
 * columns — every row renders whatever bucket set the API returns, via `BucketBar` (which has no
 * four named props, so the wrong hard-coded-column version is unwritable, not merely discouraged).
 */
export type AgeingRow = {
  customerId: string;
  /** `CUS-000123` — the customer's URL key (`customerPath`). */
  customerNumber: string;
  displayName: string;
  buckets: Record<string, number | string | null>;
  outstanding: number | string | null;
  excludesReceipts?: boolean;
  receiptsLastAppliedAt?: string | null;
  /** The API's own `outstanding > creditLimit` comparison (`CustomerBalance.OverLimit`) — never re-derived here. */
  overLimit?: boolean;
};

const TOTAL_KEY = "total";

export function bucketLabel(key: string): string {
  const body = key.replace(/^days/i, "");
  const range = /^(\d+)To(\d+)$/i.exec(body);
  if (range) return `${range[1]}–${range[2]} days`;
  const plus = /^(\d+)Plus$/i.exec(body);
  if (plus) return `${plus[1]}+ days`;
  const words = body.replace(/([a-z])([A-Z0-9])/g, "$1 $2").trim();
  return words === "" ? key : words.charAt(0).toUpperCase() + words.slice(1);
}

/** Parses the wire's `number | string`. `null` means "not reported" — never coerced to 0. */
export function moneyOf(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function displayAmount(value: number | string | null | undefined): string {
  const money = moneyOf(value);
  return money === null ? "—" : formatMoney(money);
}

/** `total` is excluded and never recomputed — the API returns it, it is not summed here. */
export function toBuckets(buckets: unknown): Bucket[] {
  if (typeof buckets !== "object" || buckets === null) return [];
  return Object.entries(buckets as Record<string, unknown>)
    .filter(([key]) => key.toLowerCase() !== TOTAL_KEY)
    .map(([key, value]) => ({
      label: bucketLabel(key),
      value: moneyOf(value as number | string | null) ?? 0,
      display: displayAmount(value as number | string | null),
    }));
}

/** `GET /admin/ageing` declares no response body, so this checks rather than casts. */
export function parseAgeingRows(body: unknown): AgeingRow[] {
  const raw = Array.isArray(body)
    ? body
    : typeof body === "object" && body !== null
      ? ((body as Record<string, unknown>).items ?? (body as Record<string, unknown>).rows ?? (body as Record<string, unknown>).customers)
      : undefined;

  if (!Array.isArray(raw)) {
    throw new Error(
      "GET /admin/ageing answered without a row array. The ageing report has nothing to render, and rendering " +
        "it empty would report that no customer owes anything.",
    );
  }

  return raw as AgeingRow[];
}
